// Real file transfers for diagnostics / log uploads and firmware downloads, the way a charge point
// does them: to and from the URL the CSMS gives (`location` / `remoteLocation`).
//
//  - HTTP(S) goes through fetch, so it works in the browser and headless. In the browser the
//    target must allow CORS. Credentials in the URL (https://user:pass@host/...) are sent as
//    Basic Auth, since fetch refuses URLs with credentials.
//  - FTP needs raw sockets, so it is only available headless (see ftp-node.ts).

/** Why a transfer failed, in the terms of the OCPP UploadLogStatus values. */
export type TransferFailure = "BadMessage" | "NotSupportedOperation" | "PermissionDenied" | "Failed";

export class TransferError extends Error {
  constructor(public readonly failure: TransferFailure, message: string) {
    super(message);
    this.name = "TransferError";
  }
}

export interface TransferFile {
  name: string;
  content: string;
  contentType: string;
}

export interface FileTransfer {
  /** For SupportedFileTransferProtocols / FileTransferProtocols, e.g. ["HTTP", "HTTPS"]. */
  readonly protocols: string[];
  upload(location: string, file: TransferFile): Promise<void>;
  download(location: string): Promise<Uint8Array>;
}

/** The FTP half of a transfer, provided by the headless runtime. */
export interface FtpClient {
  upload(url: URL, file: TransferFile): Promise<void>;
  download(url: URL): Promise<Uint8Array>;
}

export type HttpUploadMethod = "POST" | "PUT";

export const parseLocation = (location: string): URL => {
  try {
    return new URL(location);
  } catch {
    throw new TransferError("BadMessage", `Invalid location '${location}'`);
  }
};

const withoutCredentials = (url: URL) => {
  const target = new URL(url.toString());
  const auth =
    target.username || target.password
      ? `Basic ${btoa(`${decodeURIComponent(target.username)}:${decodeURIComponent(target.password)}`)}`
      : undefined;
  target.username = "";
  target.password = "";
  return { target, headers: auth ? { Authorization: auth } : undefined };
};

const failFor = (status: number, what: string): never => {
  if (status === 401 || status === 403) throw new TransferError("PermissionDenied", `${what}: HTTP ${status}`);
  throw new TransferError("Failed", `${what}: HTTP ${status}`);
};

export interface HttpTransferOptions {
  fetch?: typeof fetch;
  /** POST sends multipart/form-data (field `file`); PUT sends the raw file. */
  uploadMethod?: () => HttpUploadMethod;
  ftp?: FtpClient;
}

export const createFileTransfer = (options: HttpTransferOptions = {}): FileTransfer => {
  const doFetch = (input: string, init: RequestInit) => {
    const impl = options.fetch ?? (globalThis as any).fetch;
    if (!impl) throw new TransferError("NotSupportedOperation", "No fetch available in this runtime");
    return impl(input, init).catch((error: any) => {
      throw new TransferError("Failed", `${init.method} ${input}: ${error?.message ?? error}`);
    });
  };
  const protocol = (url: URL) => url.protocol.replace(/:$/, "").toUpperCase();
  const unsupported = (url: URL): never => {
    throw new TransferError("NotSupportedOperation", `${protocol(url)} transfers are not supported here`);
  };

  return {
    protocols: ["HTTP", "HTTPS", ...(options.ftp ? ["FTP"] : [])],

    async upload(location, file) {
      const url = parseLocation(location);
      if (url.protocol === "ftp:") return options.ftp ? options.ftp.upload(url, file) : unsupported(url);
      if (url.protocol !== "http:" && url.protocol !== "https:") return unsupported(url);
      const { target, headers } = withoutCredentials(url);
      const method = options.uploadMethod?.() ?? "POST";
      let response: Response;
      if (method === "PUT") {
        // A location ending in "/" is a directory: the file goes under its own name.
        if (target.pathname.endsWith("/")) target.pathname += encodeURIComponent(file.name);
        response = await doFetch(target.toString(), {
          method,
          headers: { ...headers, "Content-Type": file.contentType },
          body: file.content,
        });
      } else {
        const form = new FormData();
        form.append("file", new Blob([file.content], { type: file.contentType }), file.name);
        response = await doFetch(target.toString(), { method, headers, body: form });
      }
      if (!response.ok) failFor(response.status, `Upload to ${target.origin}${target.pathname}`);
    },

    async download(location) {
      const url = parseLocation(location);
      if (url.protocol === "ftp:") return options.ftp ? options.ftp.download(url) : unsupported(url);
      if (url.protocol !== "http:" && url.protocol !== "https:") return unsupported(url);
      const { target, headers } = withoutCredentials(url);
      const response = await doFetch(target.toString(), { method: "GET", headers });
      if (!response.ok) failFor(response.status, `Download of ${target.origin}${target.pathname}`);
      return new Uint8Array(await response.arrayBuffer());
    },
  };
};
